import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import Select from 'react-select';
import { fetchSupplierContracts } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { Link, useSearchParams } from "react-router";

const limit = 20;

const searchOptions = [
    { value: 'searchText', label: 'Name' },
    { value: 'supplierHccId', label: 'Supplier HCC ID' },
    { value: 'supplierNetworkHccId', label: 'Supplier Network HCC ID' },
];

const FilterChip = ({ name, value, label, onClear }) => (
    <Select
        name={name}
        className="react-select-container large-input"
        classNamePrefix="react-select"
        isClearable
        value={{ value, label }}
        options={[{ value, label }]}
        onChange={(option) => {
            if (!option) onClear();
        }}
    />
);

const statusOptions = [
    { value: 'a', label: 'Active (a)' },
];

const SupplierContracts = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlSupplierHccId = searchParams.get("supplierHccId") || "";
    const urlSupplierName = searchParams.get("supplierName") || "";
    const urlSupplierNetworkHccId = searchParams.get("supplierNetworkHccId") || "";
    const urlNetworkName = searchParams.get("networkName") || "";

    const [searchValue, setSearchValue] = useState();
    const [decouncedSearchValue, setDecouncedSearchValue] = useState();
    const [searchBy, setSearchBy] = useState(searchOptions[0]);
    const [statusFilter, setStatusFilter] = useState(null);
    const { ref, inView } = useInView();

    const dropParams = (...keys) => {
        const next = new URLSearchParams(searchParams);
        keys.forEach((k) => next.delete(k));
        setSearchParams(next);
    };

    const { data, error, fetchNextPage, hasNextPage, isLoading, isFetching, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ["supplierContracts", limit, decouncedSearchValue, searchBy?.value, statusFilter?.value, urlSupplierHccId, urlSupplierNetworkHccId],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};
            if (urlSupplierHccId) {
                params.supplierHccId = urlSupplierHccId;
            }
            if (urlSupplierNetworkHccId) {
                params.supplierNetworkHccId = urlSupplierNetworkHccId;
            }
            if (!urlSupplierHccId && !urlSupplierNetworkHccId && decouncedSearchValue && searchBy?.value) {
                params[searchBy.value] = decouncedSearchValue;
            }
            if (statusFilter?.value) {
                params.status = statusFilter.value;
            }
            return await fetchSupplierContracts({ ...params, limit });
        },
        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;
            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    const columns = ['name', 'status', 'supplierHccId', 'supplierName', 'supplierNetworkName', 'feeComponentNames', 'benefitNetworkCount', 'effectiveDate', 'terminationDate'];

    return (
        <div className={'container '}>
            <p className='title'>Supplier Contracts  ( {data?.count || 0} )</p>
            <p className="sub-title">
                Payment agreements between Centivo and a supplier and/or supplier network. Open Detail to see the fee components (pricing rules) and benefit networks on the contract.{" "}
                <Link to="/providers/guide">How this wires up in HRP</Link>
            </p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    {urlSupplierHccId && (
                        <FilterChip
                            name="supplier"
                            value={urlSupplierHccId}
                            label={`Supplier: ${urlSupplierName || urlSupplierHccId}`}
                            onClear={() => dropParams("supplierHccId", "supplierName")}
                        />
                    )}
                    {urlSupplierNetworkHccId && (
                        <FilterChip
                            name="supplierNetwork"
                            value={urlSupplierNetworkHccId}
                            label={`Supplier network: ${urlNetworkName || urlSupplierNetworkHccId}`}
                            onClear={() => dropParams("supplierNetworkHccId", "networkName")}
                        />
                    )}
                    <Select
                        name={'searchBy'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Search by'}
                        options={searchOptions}
                        value={searchBy}
                        onChange={(option) => { setSearchBy(option) }}
                    />
                    <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        autoComplete="off"
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />
                    <Select
                        name={'status'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Status'}
                        isClearable
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(option) => { setStatusFilter(option) }}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        setSearchBy(searchOptions[0]);
                        setStatusFilter(null);
                        if (urlSupplierHccId || urlSupplierNetworkHccId) setSearchParams({});
                    }}
                >
                    Clear filters
                </p>
            </div>

            {status === "loading" || status === "pending" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"supplier-contracts"}
                    columns={columns}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    searchText={decouncedSearchValue}
                    ref={ref}
                />
            )}
        </div>
    )
}

export default SupplierContracts
